import InternalChatAPI from 'dashboard/api/internalChat';
import {
  applyReaction,
  reconcileMessage,
  sortByCreatedAt,
  upsertById,
  upsertMessageById,
  scopedActions,
  ownChannelGeneration,
  withoutDeletedPreview,
} from './internalChatMessageHelpers';
import {
  threadState,
  clearThread,
  threadEvent,
  threadMutations,
  threadActions,
} from './internalChatThread';

import {
  channelAggregatesChanged,
  writeReadReceipt,
} from './internalChatReadReceipts';

import { historyActions } from './internalChatHistory';
import { navigationActions } from './internalChatNavigation';
import {
  inMessageWindow,
  mergeWindowMeta,
  windowEdges,
} from './internalChatMessageWindow';

const typingTimers = new Map();
const channelSettings = ['name', 'description', 'category_id'];
const channelAggregates = [
  'unread_count',
  'messages_count',
  'last_activity_at',
];
const channelFields = (channel, fields) =>
  Object.fromEntries(
    fields.filter(key => key in channel).map(key => [key, channel[key]])
  );

const invalidateChannelAccess = (state, channelId) => {
  state.channelRemovalGeneration[channelId] =
    (state.channelRemovalGeneration[channelId] || 0) + 1;
  [
    'channelRequestGeneration',
    'membersRequestGeneration',
    'messageRequestGeneration',
    'readRequestGeneration',
  ].forEach(key => {
    state[key][channelId] = (state[key][channelId] || 0) + 1;
  });
  Object.keys(state.draftRequestGeneration).forEach(key => {
    if (key.startsWith(`${channelId}:`)) state.draftRequestGeneration[key] += 1;
  });
  delete state.typing[channelId];
  Object.entries(state.messageTombstones).forEach(([id, owner]) => {
    if (owner.channel_id === channelId) delete state.messageTombstones[id];
  });
  typingTimers.forEach((timer, key) => {
    if (!key.startsWith(`${channelId}:`)) return;
    clearTimeout(timer);
    typingTimers.delete(key);
  });
};

const initialState = () => ({
  contextGeneration: 0,
  channelsRequestGeneration: 0,
  categoriesRequestGeneration: 0,
  channelRequestGeneration: {},
  channelRemovalGeneration: {},
  channelSelectionGeneration: 0,
  messageActionGeneration: 0,
  draftRequestGeneration: {},
  channelsRevision: 0,
  channelsListRevision: 0,
  searchRequestGeneration: 0,
  notificationsRequestGeneration: 0,
  notificationsRevision: 0,
  pendingNotifications: [],
  reactionRemovals: {},
  messageTombstones: {},
  removedChannels: {},
  readRequestGeneration: {},
  threadState: threadState(),
  channels: [],
  categories: [],
  members: {},
  membersRequestGeneration: {},
  messages: {},
  messageMeta: {},
  messageRequestGeneration: {},
  messageEvents: {},
  messageRefreshQueued: {},
  navigationTarget: null,
  knownMessageIds: {},
  selectedChannelId: null,
  thread: null,
  notifications: [],
  unreadNotifications: 0,
  drafts: {},
  typing: {},
  searchResults: [],
  searchMeta: null,
  uiFlags: {
    isFetchingChannels: false,
    isFetchingMessages: false,
    isNavigatingMessage: false,
    isSending: false,
    isCreatingChannel: false,
    isFetchingNotifications: false,
    isSearching: false,
  },
  errors: {
    channels: null,
    search: null,
    messages: null,
    navigation: null,
    send: null,
    messageAction: null,
  },
});

export const getters = {
  getChannels: state => state.channels,
  getCategories: state => state.categories,
  getMembers: state => channelId =>
    state.members[channelId] || { users: [], teams: [] },
  getSelectedChannel: state =>
    state.channels.find(channel => channel.id === state.selectedChannelId),
  getSelectedChannelId: state => state.selectedChannelId,
  getMessages: state => channelId =>
    (state.messages[channelId] || []).slice().sort(sortByCreatedAt),
  getMessageMeta: state => channelId => state.messageMeta[channelId] || {},
  getNavigationTarget: state => state.navigationTarget,
  getNotifications: state => state.notifications,
  getThread: state => state.thread,
  getThreadState: state => state.threadState,
  getUnreadNotifications: state => state.unreadNotifications,
  getDraft:
    state =>
    (channelId, parentId = null) =>
      state.drafts[`${channelId}:${parentId || 'root'}`] || null,
  getSearchResults: state => state.searchResults,
  getSearchMeta: state => state.searchMeta,
  getTypingUsers: state => channelId =>
    Object.values(state.typing[channelId] || {}),
  getUIFlags: state => state.uiFlags,
  getErrors: state => state.errors,
};

export const mutations = {
  ...threadMutations,
  RESET(state, channelsError = null) {
    typingTimers.forEach(timer => clearTimeout(timer));
    typingTimers.clear();
    const contextGeneration = state.contextGeneration + 1;
    const generation = state.threadState.generation + 1;
    Object.assign(state, initialState(), {
      contextGeneration,
      threadState: threadState(generation),
    });
    state.errors.channels = channelsError;
  },
  SET_CHANNELS(state, channels) {
    state.channelsListRevision += 1;
    const allowed = new Set(channels.map(channel => String(channel.id)));
    const removed = new Set(
      state.channels
        .filter(channel => !allowed.has(String(channel.id)))
        .map(channel => channel.id)
    );
    removed.forEach(id => {
      state.removedChannels[id] = true;
      invalidateChannelAccess(state, id);
    });
    channels.forEach(channel => {
      delete state.removedChannels[channel.id];
    });
    state.notifications = state.notifications.filter(
      item => !removed.has(item.channel_id)
    );
    state.pendingNotifications = state.pendingNotifications.filter(
      item => !removed.has(item.channel_id)
    );
    state.channels = channels;
    [
      'messages',
      'messageMeta',
      'knownMessageIds',
      'members',
      'messageEvents',
      'messageRefreshQueued',
    ].forEach(key => {
      state[key] = Object.fromEntries(
        Object.entries(state[key]).filter(([id]) => allowed.has(id))
      );
    });
    state.drafts = Object.fromEntries(
      Object.entries(state.drafts).filter(([key]) =>
        allowed.has(key.split(':')[0])
      )
    );
    state.searchResults = state.searchResults.filter(message =>
      allowed.has(String(message.channel_id))
    );
    if (
      state.selectedChannelId &&
      !allowed.has(String(state.selectedChannelId))
    ) {
      state.selectedChannelId = null;
      state.errors.messageAction = null;
      state.navigationTarget = null;
      state.uiFlags.isNavigatingMessage = false;
      state.uiFlags.isFetchingMessages = false;
      state.errors.navigation = null;
      clearThread(state);
    }
  },
  SET_CATEGORIES(state, categories) {
    state.categories = categories;
  },
  START_CATEGORIES_REQUEST(state) {
    state.categoriesRequestGeneration += 1;
  },
  START_CHANNELS_REQUEST(state) {
    state.channelsRequestGeneration += 1;
  },
  START_CHANNEL_REQUEST(state, channelId) {
    state.channelRequestGeneration[channelId] =
      (state.channelRequestGeneration[channelId] || 0) + 1;
  },
  START_READ_REQUEST(state, channelId) {
    state.readRequestGeneration[channelId] =
      (state.readRequestGeneration[channelId] || 0) + 1;
  },
  APPLY_READ_RECEIPT(state, { channelId, request, previous, unreadCount }) {
    if (request !== state.readRequestGeneration[channelId] || !previous) return;
    let applied = false;
    state.channels = state.channels.map(channel => {
      if (channel.id !== channelId) return channel;
      if (channelAggregatesChanged(channel, previous)) return channel;
      applied = true;
      return { ...channel, unread_count: unreadCount };
    });
    if (applied) state.channelsRevision += 1;
  },
  SET_MEMBERS(state, { channelId, members }) {
    state.members = { ...state.members, [channelId]: members };
  },
  START_MEMBERS_REQUEST(state, channelId) {
    state.membersRequestGeneration[channelId] =
      (state.membersRequestGeneration[channelId] || 0) + 1;
  },
  SET_SELECTED_CHANNEL(state, channelId) {
    state.channelSelectionGeneration += 1;
    if (state.selectedChannelId !== channelId)
      state.errors.messageAction = null;
    state.selectedChannelId = channelId;
    state.navigationTarget = null;
    state.messageEvents = {};
    state.messageRefreshQueued = {};
    state.uiFlags.isNavigatingMessage = false;
    state.uiFlags.isFetchingMessages = false;
    state.errors.navigation = null;
    clearThread(state);
  },
  START_MESSAGE_ACTION(state) {
    state.messageActionGeneration += 1;
    state.errors.messageAction = null;
  },
  START_MESSAGE_REQUEST(state, channelId) {
    state.messageRequestGeneration[channelId] =
      (state.messageRequestGeneration[channelId] || 0) + 1;
    state.messageEvents[channelId] = [];
  },
  END_MESSAGE_REQUEST(state, channelId) {
    delete state.messageEvents[channelId];
  },
  QUEUE_MESSAGE_REFRESH(state, { channelId, queued }) {
    if (queued) state.messageRefreshQueued[channelId] = true;
    else delete state.messageRefreshQueued[channelId];
  },
  SET_NAVIGATION_TARGET(state, target) {
    state.navigationTarget = target;
  },
  SET_MESSAGES(state, { channelId, messages, meta = {} }) {
    const records = messages.map(message => reconcileMessage(state, message));
    state.messages = { ...state.messages, [channelId]: records };
    state.messageMeta = {
      ...state.messageMeta,
      [channelId]: windowEdges(messages.slice().sort(sortByCreatedAt), meta),
    };
    state.knownMessageIds[channelId] = [
      ...new Set([
        ...(state.knownMessageIds[channelId] || []),
        ...messages.map(message => message.id),
      ]),
    ];
  },
  MERGE_MESSAGES(state, { channelId, messages, meta = {}, newer = false }) {
    const records = state.messages[channelId] || [];
    const merged = messages
      .map(message => reconcileMessage(state, message))
      .reduce(upsertMessageById, records);
    state.messages = { ...state.messages, [channelId]: merged };
    state.messageMeta = {
      ...state.messageMeta,
      [channelId]: windowEdges(
        merged.slice().sort(sortByCreatedAt),
        mergeWindowMeta(state.messageMeta[channelId] || {}, meta, newer)
      ),
    };
    state.knownMessageIds[channelId] = [
      ...new Set([
        ...(state.knownMessageIds[channelId] || []),
        ...messages.map(message => message.id),
      ]),
    ];
  },
  SET_THREAD(state, thread) {
    if (thread) state.thread = thread;
    else clearThread(state);
  },
  SET_DRAFT(state, { channelId, parentId, draft }) {
    const key = `${channelId}:${parentId || 'root'}`;
    state.draftRequestGeneration[key] =
      (state.draftRequestGeneration[key] || 0) + 1;
    if (draft) state.drafts = { ...state.drafts, [key]: draft };
    else {
      const drafts = { ...state.drafts };
      delete drafts[key];
      state.drafts = drafts;
    }
  },
  START_DRAFT_REQUEST(state, { channelId, parentId }) {
    const key = `${channelId}:${parentId || 'root'}`;
    state.draftRequestGeneration[key] =
      (state.draftRequestGeneration[key] || 0) + 1;
  },
  SET_SEARCH(state, { results, meta }) {
    state.searchResults = results.filter(
      message => !state.messageTombstones[message.id]
    );
    state.searchMeta = meta;
  },
  START_SEARCH_REQUEST(state) {
    state.searchRequestGeneration += 1;
    state.searchResults = [];
    state.searchMeta = null;
    state.uiFlags.isSearching = false;
    state.errors.search = null;
  },
  SET_TYPING(state, { channelId, user }) {
    state.typing = {
      ...state.typing,
      [channelId]: { ...(state.typing[channelId] || {}), [user.id]: user },
    };
  },
  CLEAR_TYPING_USER(state, { channelId, userId }) {
    const users = { ...(state.typing[channelId] || {}) };
    delete users[userId];
    const typing = { ...state.typing };
    if (Object.keys(users).length) typing[channelId] = users;
    else delete typing[channelId];
    state.typing = typing;
  },
  CLEAR_TYPING(state) {
    state.typing = {};
  },
  UPSERT_MESSAGE(state, incoming) {
    const message = reconcileMessage(state, incoming);
    state.knownMessageIds[message.channel_id] = [
      ...new Set([
        ...(state.knownMessageIds[message.channel_id] || []),
        message.id,
      ]),
    ];
    if (
      !message.parent_id ||
      message.content_attributes?.also_send_in_channel
    ) {
      const records = state.messages[message.channel_id] || [];
      const meta = state.messageMeta[message.channel_id] || {};
      state.messageEvents[message.channel_id]?.push({ message });
      if (
        inMessageWindow(message, records.slice().sort(sortByCreatedAt), meta)
      ) {
        const updated = upsertMessageById(records, message);
        state.messages = { ...state.messages, [message.channel_id]: updated };
        state.messageMeta[message.channel_id] = windowEdges(
          updated.slice().sort(sortByCreatedAt),
          meta
        );
      }
    }
    threadEvent(state, { message });
  },
  UPSERT_CHANNEL(state, channel) {
    delete state.removedChannels[channel.id];
    state.channelsRevision += 1;
    state.channels = upsertById(state.channels, channel);
  },
  REMOVE_CHANNEL(state, channelId) {
    state.removedChannels[channelId] = true;
    invalidateChannelAccess(state, channelId);
    state.channelsRevision += 1;
    state.channels = state.channels.filter(channel => channel.id !== channelId);
    state.notifications = state.notifications.filter(
      item => item.channel_id !== channelId
    );
    state.pendingNotifications = state.pendingNotifications.filter(
      item => item.channel_id !== channelId
    );
    Object.entries(state.reactionRemovals).forEach(([messageId, removals]) => {
      if (Object.values(removals).some(item => item.channel_id === channelId))
        delete state.reactionRemovals[messageId];
    });
    [
      'messages',
      'messageMeta',
      'knownMessageIds',
      'members',
      'messageEvents',
      'messageRefreshQueued',
      'typing',
    ].forEach(key => {
      delete state[key][channelId];
    });
    Object.keys(state.drafts).forEach(key => {
      if (key.startsWith(`${channelId}:`)) delete state.drafts[key];
    });
    state.searchResults = state.searchResults.filter(
      message => message.channel_id !== channelId
    );
    state.searchRequestGeneration += 1;
    state.uiFlags.isSearching = false;
    state.errors.search = null;
    if (state.selectedChannelId === channelId) {
      state.selectedChannelId = null;
      state.errors.messageAction = null;
      state.navigationTarget = null;
      state.uiFlags.isNavigatingMessage = false;
      state.uiFlags.isFetchingMessages = false;
      state.errors.navigation = null;
      clearThread(state);
    }
  },
  START_NOTIFICATIONS_REQUEST(state) {
    state.notificationsRequestGeneration += 1;
  },
  SET_NOTIFICATIONS(state, { notifications, unreadCount }) {
    const pending = state.pendingNotifications.filter(notification =>
      state.channels.some(channel => channel.id === notification.channel_id)
    );
    const records = pending.reduce((items, notification) => {
      const current = items.find(item => item.id === notification.id);
      const value = current?.read_at
        ? { ...notification, read_at: current.read_at }
        : notification;
      return upsertById(items, value);
    }, notifications);
    state.notifications = records.map(record => {
      const previous = state.notifications.find(item => item.id === record.id);
      const value = previous?.read_at
        ? { ...record, read_at: previous.read_at }
        : record;
      return withoutDeletedPreview(state, value);
    });
    state.pendingNotifications = [];
    state.unreadNotifications = unreadCount;
  },
  ADD_NOTIFICATION(state, notification) {
    const previous = state.notifications.find(
      item => item.id === notification.id
    );
    const value = previous?.read_at
      ? { ...notification, read_at: previous.read_at }
      : notification;
    const preview = withoutDeletedPreview(state, value);
    state.notifications = upsertById(state.notifications, preview);
    state.pendingNotifications = upsertById(
      state.pendingNotifications,
      preview
    );
    state.notificationsRevision += 1;
    if (!previous && !value.read_at) state.unreadNotifications += 1;
    if (previous && !previous.read_at && value.read_at)
      state.unreadNotifications -= 1;
  },
  UPDATE_CHANNEL_ACTIVITY(state, { message }) {
    state.channelsRevision += 1;
    state.channels = state.channels.map(channel =>
      channel.id === message.channel_id
        ? {
            ...channel,
            messages_count: (channel.messages_count || 0) + 1,
            last_activity_at: message.created_at,
          }
        : channel
    );
  },
  UPSERT_REACTION(state, payload) {
    const key = `${payload.reaction.user_id}:${payload.reaction.emoji}`;
    if (
      state.reactionRemovals[payload.messageId]?.[key]?.id >=
      payload.reaction.id
    )
      return;
    Object.values(state.messageEvents).forEach(events => events.push(payload));
    Object.keys(state.messages).forEach(channelId => {
      state.messages[channelId] = state.messages[channelId].map(message =>
        applyReaction(message, payload)
      );
    });
    threadEvent(state, payload);
  },
  REMOVE_REACTION(state, payload) {
    const event = { ...payload, removed: true };
    const previous = {
      id: payload.messageId,
      reactionRemovals: state.reactionRemovals[payload.messageId],
    };
    state.reactionRemovals[payload.messageId] = applyReaction(previous, {
      ...event,
      reaction: { ...payload.reaction, channel_id: payload.channelId },
    }).reactionRemovals;
    Object.values(state.messageEvents).forEach(events => events.push(event));
    Object.keys(state.messages).forEach(channelId => {
      state.messages[channelId] = state.messages[channelId].map(message =>
        applyReaction(message, event)
      );
    });
    threadEvent(state, event);
  },
  SET_UI_FLAG(state, values) {
    state.uiFlags = { ...state.uiFlags, ...values };
  },
  SET_ERROR(state, values) {
    state.errors = { ...state.errors, ...values };
  },
};

export const actions = {
  ...threadActions,
  ...historyActions,
  ...navigationActions,
  reset({ commit }) {
    commit('RESET');
  },
  async initialize({ dispatch }) {
    await Promise.all([
      dispatch('fetchChannels'),
      dispatch('fetchCategories'),
      dispatch('fetchNotifications'),
    ]);
  },
  async refreshAfterReconnect({ dispatch, state }) {
    try {
      await dispatch('initialize');
      if (state.selectedChannelId) {
        await Promise.all([
          dispatch('refreshMessages', state.selectedChannelId),
          dispatch('fetchMembers', state.selectedChannelId),
        ]);
      }
      if (state.selectedChannelId && state.thread?.parent?.id) {
        await dispatch('refreshThread');
      }
    } catch {
      // The route can be disabled while the cable connection remains active.
    }
  },
  async fetchChannels({ commit, dispatch, state }) {
    const revision = state.channelsRevision;
    const generation = state.contextGeneration;
    commit('START_CHANNELS_REQUEST');
    const request = state.channelsRequestGeneration;
    const isCurrent = () =>
      generation === state.contextGeneration &&
      request === state.channelsRequestGeneration;
    commit('SET_UI_FLAG', { isFetchingChannels: true });
    commit('SET_ERROR', { channels: null });
    try {
      const response = await InternalChatAPI.getChannels();
      if (!isCurrent()) return null;
      if (revision !== state.channelsRevision) return dispatch('fetchChannels');
      commit('SET_CHANNELS', response.data);
      return response.data;
    } catch (error) {
      if (!isCurrent()) return null;
      if ([401, 403].includes(error.response?.status)) commit('RESET', error);
      else commit('SET_ERROR', { channels: error });
      throw error;
    } finally {
      if (isCurrent()) commit('SET_UI_FLAG', { isFetchingChannels: false });
    }
  },
  async fetchChannel({ commit, dispatch, state }, channelId) {
    const previous = state.channels.find(channel => channel.id === channelId);
    if (!previous) return null;
    const generation = state.contextGeneration;
    commit('START_CHANNEL_REQUEST', channelId);
    const request = state.channelRequestGeneration[channelId];
    const isCurrent = () =>
      generation === state.contextGeneration &&
      request === state.channelRequestGeneration[channelId];
    try {
      const response = await InternalChatAPI.getChannel(channelId);
      if (!isCurrent()) return null;
      const channel = state.channels.find(item => item.id === channelId);
      if (!channel) return null;
      if (channel !== previous) return dispatch('fetchChannel', channelId);
      commit('UPSERT_CHANNEL', {
        id: channelId,
        ...channelFields(response.data, channelAggregates),
      });
      return response.data;
    } catch (error) {
      if (!isCurrent()) return null;
      if ([401, 403, 404].includes(error.response?.status)) {
        try {
          return await dispatch('fetchChannels');
        } catch {
          // The list action records the error and revokes denied access.
          return null;
        }
      }
      commit('SET_ERROR', { channels: error });
      return null;
    }
  },
  async selectChannel({ commit, dispatch }, channelId) {
    commit('SET_SELECTED_CHANNEL', channelId);
    await Promise.all([
      dispatch('fetchMessages', channelId),
      dispatch('fetchMembers', channelId),
      dispatch('markRead', channelId),
      dispatch('fetchDraft', { channelId }),
    ]);
  },
  async fetchCategories({ commit, state }) {
    const generation = state.contextGeneration;
    commit('START_CATEGORIES_REQUEST');
    const request = state.categoriesRequestGeneration;
    try {
      const response = await InternalChatAPI.getCategories();
      if (
        generation !== state.contextGeneration ||
        request !== state.categoriesRequestGeneration
      )
        return null;
      commit('SET_CATEGORIES', response.data);
      return response.data;
    } catch {
      return [];
    }
  },
  async fetchMembers({ commit, state }, channelId) {
    const generation = state.contextGeneration;
    commit('START_MEMBERS_REQUEST', channelId);
    const request = state.membersRequestGeneration[channelId];
    const isCurrent = () =>
      generation === state.contextGeneration &&
      request === state.membersRequestGeneration[channelId] &&
      state.channels.some(channel => channel.id === channelId);
    try {
      const response = await InternalChatAPI.getMembers(channelId);
      if (!isCurrent()) return null;
      commit('SET_MEMBERS', { channelId, members: response.data });
      return response.data;
    } catch {
      if (!isCurrent()) return null;
      const empty = { users: [], teams: [] };
      commit('SET_MEMBERS', { channelId, members: empty });
      return empty;
    }
  },
  async fetchDraft({ commit, state }, { channelId, parentId = null }) {
    const generation = state.threadState.generation;
    const selection = state.channelSelectionGeneration;
    const key = `${channelId}:${parentId || 'root'}`;
    commit('START_DRAFT_REQUEST', { channelId, parentId });
    const request = state.draftRequestGeneration[key];
    const response = await InternalChatAPI.getDraft(channelId, parentId);
    if (
      state.selectedChannelId !== channelId ||
      selection !== state.channelSelectionGeneration ||
      request !== state.draftRequestGeneration[key] ||
      (parentId && generation !== state.threadState.generation)
    )
      return null;
    commit('SET_DRAFT', { channelId, parentId, draft: response.data });
    return response.data;
  },
  async saveDraft(
    { commit, state },
    { channelId, parentId = null, content, contentAttributes = {} }
  ) {
    const current = ownChannelGeneration(state, channelId);
    if (!content && !Object.keys(contentAttributes).length) {
      await InternalChatAPI.deleteDraft(channelId, parentId);
      if (!current()) return null;
      commit('SET_DRAFT', { channelId, parentId, draft: null });
      return null;
    }
    const response = await InternalChatAPI.saveDraft(channelId, {
      parentId,
      content,
      contentAttributes,
    });
    if (!current()) return null;
    commit('SET_DRAFT', { channelId, parentId, draft: response.data });
    return response.data;
  },
  async deleteDraft({ commit, state }, { channelId, parentId = null }) {
    const current = ownChannelGeneration(state, channelId);
    await InternalChatAPI.deleteDraft(channelId, parentId);
    if (!current()) return null;
    commit('SET_DRAFT', { channelId, parentId, draft: null });
    return null;
  },
  async markRead(context, channelId) {
    try {
      return await writeReadReceipt(context, channelId);
    } catch {
      // A channel can disappear while it is selected. Realtime refresh reconciles it.
      return null;
    }
  },
  markUnread(context, channelId) {
    return writeReadReceipt(context, channelId, true);
  },
  async sendMessage({ commit, dispatch, state }, payload) {
    const current = ownChannelGeneration(state, payload.channelId);
    const selection = state.channelSelectionGeneration;
    const threadGeneration = state.threadState.generation;
    commit('SET_UI_FLAG', { isSending: true });
    commit('SET_ERROR', { send: null });
    try {
      const response = await InternalChatAPI.createMessage(
        payload.channelId,
        payload
      );
      if (
        !current() ||
        !state.channels.some(channel => channel.id === payload.channelId)
      )
        return response.data;
      commit('UPSERT_MESSAGE', response.data);
      await dispatch('fetchChannel', payload.channelId);
      if (
        selection === state.channelSelectionGeneration &&
        state.selectedChannelId === payload.channelId
      ) {
        try {
          if (
            payload.parentId &&
            threadGeneration === state.threadState.generation &&
            state.thread?.meta.has_more_after
          ) {
            commit('SET_NAVIGATION_TARGET', null);
            await dispatch('showLatestThread');
          } else if (
            !payload.parentId &&
            state.messageMeta[payload.channelId]?.has_more_after
          ) {
            commit('SET_NAVIGATION_TARGET', null);
            await dispatch('fetchMessages', payload.channelId);
          }
        } catch {
          // The POST is confirmed; history exposes its retry error without repeating the send.
        }
      }
      return response.data;
    } catch (error) {
      commit('SET_ERROR', { send: error });
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isSending: false });
    }
  },
  async updateMessage(
    { commit, state },
    {
      channelId,
      messageId,
      content,
      contentFormat,
      mentionedUserIds = [],
      mentionedTeamIds = [],
      mentionAll = false,
    }
  ) {
    const current = ownChannelGeneration(state, channelId);
    commit('START_MESSAGE_ACTION');
    const action = state.messageActionGeneration;
    const selection = state.channelSelectionGeneration;
    try {
      const response = await InternalChatAPI.updateMessage(
        channelId,
        messageId,
        {
          content,
          ...(contentFormat ? { content_format: contentFormat } : {}),
          mentioned_user_ids: mentionedUserIds,
          mentioned_team_ids: mentionedTeamIds,
          mention_all: mentionAll,
        }
      );
      if (current() && state.channels.some(channel => channel.id === channelId))
        commit('UPSERT_MESSAGE', response.data);
      return response.data;
    } catch (error) {
      if (
        current() &&
        state.selectedChannelId === channelId &&
        selection === state.channelSelectionGeneration &&
        action === state.messageActionGeneration
      )
        commit('SET_ERROR', { messageAction: error });
      throw error;
    }
  },
  async deleteMessage({ commit, state }, { channelId, messageId }) {
    const current = ownChannelGeneration(state, channelId);
    commit('START_MESSAGE_ACTION');
    const action = state.messageActionGeneration;
    const selection = state.channelSelectionGeneration;
    try {
      const response = await InternalChatAPI.deleteMessage(
        channelId,
        messageId
      );
      if (current() && state.channels.some(channel => channel.id === channelId))
        commit('UPSERT_MESSAGE', response.data);
      return response.data;
    } catch (error) {
      if (
        current() &&
        state.selectedChannelId === channelId &&
        selection === state.channelSelectionGeneration &&
        action === state.messageActionGeneration
      )
        commit('SET_ERROR', { messageAction: error });
      throw error;
    }
  },
  async createChannel({ commit }, payload) {
    commit('SET_UI_FLAG', { isCreatingChannel: true });
    try {
      const response = await InternalChatAPI.createChannel(payload);
      commit('UPSERT_CHANNEL', response.data);
      commit('SET_SELECTED_CHANNEL', response.data.id);
      return response.data;
    } finally {
      commit('SET_UI_FLAG', { isCreatingChannel: false });
    }
  },
  async updateChannel({ commit, dispatch, state }, { channelId, attributes }) {
    const response = await InternalChatAPI.updateChannel(channelId, attributes);
    if (response.status === 204) {
      commit('REMOVE_CHANNEL', channelId);
      // The mutation is confirmed; a later index error must not repeat it.
      await dispatch('fetchChannels').catch(() => null);
      return null;
    }
    if (state.channels.some(channel => channel.id === channelId))
      commit('UPSERT_CHANNEL', {
        id: channelId,
        ...channelFields(response.data, channelSettings),
      });
    return response.data;
  },
  async archiveChannel({ commit, dispatch }, channelId) {
    await InternalChatAPI.archiveChannel(channelId);
    commit('REMOVE_CHANNEL', channelId);
    await dispatch('fetchChannels').catch(() => null);
  },
  async unarchiveChannel({ commit, dispatch, state }, channelId) {
    const response = await InternalChatAPI.unarchiveChannel(channelId);
    if (response.status === 204) commit('REMOVE_CHANNEL', channelId);
    else {
      const known = state.channels.some(channel => channel.id === channelId);
      commit(
        'UPSERT_CHANNEL',
        known ? { id: channelId, status: response.data.status } : response.data
      );
    }
    await dispatch('fetchChannels').catch(() => null);
    return response.status === 204 ? null : response.data;
  },
  async deleteChannel({ commit }, channelId) {
    await InternalChatAPI.deleteChannel(channelId);
    commit('REMOVE_CHANNEL', channelId);
  },
  async createCategory({ dispatch }, attributes) {
    const response = await InternalChatAPI.createCategory(attributes);
    await dispatch('fetchCategories');
    return response.data;
  },
  async updateCategory({ dispatch }, { categoryId, attributes }) {
    const response = await InternalChatAPI.updateCategory(
      categoryId,
      attributes
    );
    await dispatch('fetchCategories');
    return response.data;
  },
  async deleteCategory({ dispatch }, categoryId) {
    await InternalChatAPI.deleteCategory(categoryId);
    await dispatch('fetchCategories');
    await dispatch('fetchChannels');
  },
  async addMember({ dispatch }, { channelId, attributes }) {
    const response = await InternalChatAPI.addMember(channelId, attributes);
    await dispatch('fetchMembers', channelId);
    await dispatch('fetchChannels');
    return response.data;
  },
  async removeMember({ dispatch }, { channelId, membershipId, type = 'user' }) {
    await InternalChatAPI.removeMember(channelId, membershipId, type);
    await dispatch('fetchMembers', channelId);
    await dispatch('fetchChannels');
  },
  async fetchNotifications({ commit, dispatch, state }) {
    const listRevision = state.channelsListRevision;
    const generation = state.contextGeneration;
    const removalGenerations = { ...state.channelRemovalGeneration };
    const revision = state.notificationsRevision;
    const channels = state.channels.map(channel => channel.id);
    commit('START_NOTIFICATIONS_REQUEST');
    const request = state.notificationsRequestGeneration;
    const isCurrent = () =>
      generation === state.contextGeneration &&
      request === state.notificationsRequestGeneration;
    commit('SET_UI_FLAG', { isFetchingNotifications: true });
    try {
      const response = await InternalChatAPI.getNotifications();
      if (!isCurrent()) return null;
      const visible = new Set(state.channels.map(channel => channel.id));
      const accessChanged = Object.entries(state.channelRemovalGeneration).some(
        ([id, value]) => value !== (removalGenerations[id] || 0)
      );
      if (
        accessChanged ||
        revision !== state.notificationsRevision ||
        channels.some(id => !visible.has(id)) ||
        (listRevision !== state.channelsListRevision &&
          response.data.notifications.some(
            notification =>
              !channels.includes(notification.channel_id) &&
              !visible.has(notification.channel_id)
          ))
      )
        return dispatch('fetchNotifications');
      commit('SET_NOTIFICATIONS', {
        notifications: response.data.notifications,
        unreadCount: response.data.unread_count,
      });
      return response.data;
    } catch (error) {
      if (!isCurrent()) return null;
      throw error;
    } finally {
      if (isCurrent())
        commit('SET_UI_FLAG', { isFetchingNotifications: false });
    }
  },
  clearSearch({ commit }) {
    commit('START_SEARCH_REQUEST');
  },
  async search({ commit, dispatch, state }, query) {
    const listRevision = state.channelsListRevision;
    const channels = state.channels.map(channel => channel.id);
    const removalGenerations = { ...state.channelRemovalGeneration };
    const generation = state.contextGeneration;
    commit('START_SEARCH_REQUEST');
    const request = state.searchRequestGeneration;
    const isCurrent = () =>
      generation === state.contextGeneration &&
      request === state.searchRequestGeneration;
    if (!query || query.trim().length < 2) {
      return [];
    }

    commit('SET_UI_FLAG', { isSearching: true });
    try {
      const response = await InternalChatAPI.search(query);
      if (!isCurrent()) return [];
      const visible = new Set(state.channels.map(channel => channel.id));
      if (
        listRevision !== state.channelsListRevision &&
        response.data.results.some(
          message =>
            !channels.includes(message.channel_id) &&
            !visible.has(message.channel_id)
        )
      )
        return dispatch('search', query);
      const removed = new Set(channels.filter(id => !visible.has(id)));
      const results = response.data.results.filter(
        message =>
          !state.messageTombstones[message.id] &&
          !removed.has(message.channel_id) &&
          (removalGenerations[message.channel_id] || 0) ===
            (state.channelRemovalGeneration[message.channel_id] || 0)
      );
      commit('SET_SEARCH', { ...response.data, results });
      return results;
    } catch (error) {
      if (!isCurrent()) return [];
      commit('SET_ERROR', { search: error });
      throw error;
    } finally {
      if (isCurrent()) commit('SET_UI_FLAG', { isSearching: false });
    }
  },
  async setTyping(_context, { channelId, state }) {
    try {
      await InternalChatAPI.setTyping(channelId, state);
    } catch {
      // Typing is transient and must never block message composition.
    }
  },
  typingChanged({ commit, state }, data) {
    if (state.removedChannels[data.channel_id]) return;
    const channelId = data.channel_id;
    const user = data.user || { id: data.user_id };
    if (!channelId || !user.id) return;

    const key = `${channelId}:${user.id}`;
    clearTimeout(typingTimers.get(key));
    typingTimers.delete(key);
    if (data.state === 'off') {
      commit('CLEAR_TYPING_USER', { channelId, userId: user.id });
      return;
    }

    commit('SET_TYPING', { channelId, user });
    typingTimers.set(
      key,
      setTimeout(() => {
        commit('CLEAR_TYPING_USER', { channelId, userId: user.id });
        typingTimers.delete(key);
      }, 5000)
    );
  },
  clearTyping({ commit }) {
    typingTimers.forEach(timer => clearTimeout(timer));
    typingTimers.clear();
    commit('CLEAR_TYPING');
  },
  async readNotification({ commit, dispatch, state }, notificationId) {
    const removalGenerations = { ...state.channelRemovalGeneration };
    const response = await InternalChatAPI.readNotification(notificationId);
    const channelId = response.data.channel_id;
    if (
      (removalGenerations[channelId] || 0) ===
        (state.channelRemovalGeneration[channelId] || 0) &&
      state.channels.some(channel => channel.id === channelId)
    )
      commit('ADD_NOTIFICATION', response.data);
    await dispatch('fetchNotifications');
  },
  async readAllNotifications({ dispatch }) {
    await InternalChatAPI.readAllNotifications();
    await dispatch('fetchNotifications');
  },
  messageCreated({ commit, dispatch, state }, data) {
    if (state.removedChannels[data.channel_id]) return null;
    const isKnown = (state.knownMessageIds[data.channel_id] || []).includes(
      data.id
    );
    commit('UPSERT_MESSAGE', data);
    if (!isKnown) commit('UPDATE_CHANNEL_ACTIVITY', { message: data });
    const refresh = dispatch('fetchChannel', data.channel_id);
    if (data.internal_chat_notification) {
      const notification = data.internal_chat_notification;
      const oldest = state.notifications.slice().sort(sortByCreatedAt)[0];
      const delayed =
        oldest &&
        sortByCreatedAt(notification, oldest) < 0 &&
        !state.notifications.some(item => item.id === notification.id);
      commit('ADD_NOTIFICATION', notification);
      if (delayed && !state.uiFlags.isFetchingNotifications)
        return Promise.all([refresh, dispatch('fetchNotifications')]);
    }
    return refresh;
  },
  messageUpdated({ commit, dispatch, state }, data) {
    if (state.removedChannels[data.channel_id]) return null;
    commit('UPSERT_MESSAGE', data);
    // Message revisions do not order unread: receipts and other messages also change it.
    return dispatch('fetchChannel', data.channel_id);
  },
  async createReaction({ commit, state }, { channelId, messageId, emoji }) {
    const current = ownChannelGeneration(state, channelId);
    const response = await InternalChatAPI.createReaction(
      channelId,
      messageId,
      emoji
    );
    if (!current()) return null;
    commit('UPSERT_REACTION', {
      channelId,
      messageId,
      reaction: response.data,
    });
    return response.data;
  },
  async deleteReaction(
    { commit, state },
    { channelId, messageId, reactionId, emoji, userId }
  ) {
    const current = ownChannelGeneration(state, channelId);
    await InternalChatAPI.deleteReaction(channelId, messageId, reactionId);
    if (!current()) return null;
    commit('REMOVE_REACTION', {
      channelId,
      messageId,
      reaction: { id: reactionId, emoji, user_id: userId },
    });
    return null;
  },
  reactionChanged({ commit, state }, data) {
    if (state.removedChannels[data.channel_id]) return;
    if (data.action === 'deleted') {
      commit('REMOVE_REACTION', {
        messageId: data.message_id,
        channelId: data.channel_id,
        reaction: data.reaction,
      });
    } else {
      commit('UPSERT_REACTION', {
        messageId: data.message_id,
        channelId: data.channel_id,
        reaction: data.reaction,
      });
    }
  },
  async channelChanged({ dispatch, commit, state }, data) {
    if (data.action === 'deleted') commit('REMOVE_CHANNEL', data.channel_id);
    await dispatch('fetchChannels');
    await dispatch('fetchNotifications');
    if (state.selectedChannelId === data.channel_id) {
      await Promise.all([
        dispatch('refreshMessages', data.channel_id),
        dispatch('fetchMembers', data.channel_id),
      ]);
    }
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions: scopedActions(actions),
  mutations,
};
